import React, { useCallback, useMemo, useState, useRef, useEffect, Suspense, lazy, ComponentType } from 'react';
import { Button } from 'src/components/ui/Button';
import Icon from 'src/components/Icon';
import Jnx, { JnxExpr } from 'src/util/jnx';
import { type SideChannel, useSideChannelSubscription } from 'src/util/useSideChannel';
import ReportsApi, { DashboardEmbedToken } from 'src/api/ReportsApi';
import useLoader from 'src/util/useLoader';
import DashboardLoadFailed from '../Reports/DashboardLoadFailed';

// ---------------------------------------------------------------------------
// Lazy-load SupersetDashboard with exponential-backoff retry for ChunkLoadError
// ---------------------------------------------------------------------------

interface SupersetDashboardProps {
  embeddedUuid: string;
  // The token already fetched for this render; handed to the SDK's first call instead of minting again.
  initialToken?: string;
  // Called by the embed SDK on mount and to re-mint the guest token as the current one nears expiry.
  // Required: there is no correct fallback - see the comment on `refetchToken` below.
  fetchToken: () => Promise<string>;
  url: string;
  height?: string | number;
  hideTitle?: boolean;
  hideTab?: boolean;
  hideChartControls?: boolean;
}

type LazyModule = { default: ComponentType<SupersetDashboardProps> };

function retryImport(fn: () => Promise<LazyModule>, retriesLeft = 5, interval = 1000): Promise<LazyModule> {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(e => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(e);
            return;
          }
          retryImport(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

const loadSupersetDashboard = (): Promise<LazyModule> =>
  import('../Reports/Dashboard').then(module => ({
    default: (module as unknown as { SupersetDashboard: ComponentType<SupersetDashboardProps> }).SupersetDashboard,
  }));

const SupersetDashboard = lazy(() => retryImport(loadSupersetDashboard));

// ---------------------------------------------------------------------------
// Constraint types
// ---------------------------------------------------------------------------

interface LiteralClause {
  type: 'literal';
  value: string;
}

interface LiteralKvClause {
  type: 'literal_kv';
  field: string;
  value: unknown;
}

interface ExpressionClause {
  type: 'expression';
  field: string;
  jnx: Jnx;
}

type Clause = LiteralClause | LiteralKvClause | ExpressionClause;

interface ConstraintConfig {
  dataset: string;
  clauses: Clause[];
}

interface RawConstraint {
  dataset: string;
  clause?: string | Record<string, unknown>;
}

interface ResolvedConstraint {
  dataset: string;
  clause: string;
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

interface SupersetDashboardFieldProps {
  formContext?: {
    sideChannel?: SideChannel<unknown[]>;
    [key: string]: unknown;
  };
  schema: {
    title: string;
  };
  uiSchema: {
    'ui:dashboardUuid'?: string;
    'ui:constraints'?: RawConstraint | RawConstraint[];
    'ui:displayMode'?: 'popup' | 'inline';
    'ui:height'?: string | number;
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SupersetDashboardField(props: SupersetDashboardFieldProps) {
  const {
    formContext = {},
    schema: { title },
    uiSchema: {
      'ui:dashboardUuid': dashboardUuid,
      'ui:constraints': constraints,
      'ui:displayMode': displayMode = 'popup',
      'ui:height': uiHeight,
    },
  } = props;

  const { sideChannel } = formContext;
  const rootFormData = useSideChannelSubscription(sideChannel as SideChannel<unknown[]>, 0);

  const windowRef = useRef<Window | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, error, loadFn] = useLoader<DashboardEmbedToken>();
  const [tokenData, setTokenData] = useState<DashboardEmbedToken | null>(null);

  // Build constraint configs from uiSchema
  const constraintsConfig = useMemo((): ConstraintConfig[] => {
    if (!constraints) return [];

    const list = Array.isArray(constraints) ? constraints : [constraints];

    return list.map(c => {
      const config: ConstraintConfig = { dataset: c.dataset, clauses: [] };

      if (typeof c.clause === 'string') {
        config.clauses.push({ type: 'literal', value: c.clause });
      } else if (typeof c.clause === 'object' && c.clause !== null) {
        Object.entries(c.clause).forEach(([key, value]) => {
          if (key.startsWith('expr:')) {
            const fieldName = key.replace(/^expr:/, '');
            config.clauses.push({ type: 'expression', field: fieldName, jnx: new Jnx(value as string) });
          } else {
            config.clauses.push({ type: 'literal_kv', field: key, value });
          }
        });
      }
      return config;
    });
  }, [constraints]);

  // Resolve constraints against current form data
  const resolvedConstraints = useMemo((): ResolvedConstraint[] | null => {
    if (!constraintsConfig.length) return null;

    const results = constraintsConfig
      .map(config => {
        const parts: string[] = [];

        config.clauses.forEach(clauseItem => {
          if (clauseItem.type === 'literal') {
            parts.push(clauseItem.value);
          } else if (clauseItem.type === 'literal_kv') {
            const val = typeof clauseItem.value === 'string' ? `'${clauseItem.value}'` : clauseItem.value;
            parts.push(`${clauseItem.field} = ${val}`);
          } else {
            // expression clause
            try {
              const evalResult = clauseItem.jnx.eval(rootFormData || {}, '', {
                root: rootFormData,
                formContext,
              });
              const val = typeof evalResult === 'string' ? `'${evalResult}'` : evalResult;
              parts.push(`${clauseItem.field} = ${val}`);
            } catch (e) {
              console.warn(
                `Error evaluating Superset constraint expression for ${clauseItem.field}`,
                e instanceof Error ? e.message : e
              );
            }
          }
        });

        const finalClause = parts.join(' AND ');
        return finalClause ? { dataset: config.dataset, clause: finalClause } : null;
      })
      .filter((c): c is ResolvedConstraint => c !== null);

    return results;
  }, [constraintsConfig, rootFormData, formContext]);

  // Latest resolved constraints, read through a ref so `refetchToken` can stay identity-stable.
  // The SDK's `fetchGuestToken` is a dependency of the iframe's ref callback, so a callback that
  // changed with every form edit would tear the iframe down and re-embed the dashboard on each
  // keystroke.
  const resolvedConstraintsRef = useRef<ResolvedConstraint[] | null>(resolvedConstraints);
  resolvedConstraintsRef.current = resolvedConstraints;

  // Re-mint on demand: the embed SDK calls this as the current token nears expiry (guest tokens
  // carry a 300s TTL), and replaying the first token instead would 401 every request from the
  // 5-minute mark on.
  const refetchToken = useCallback(async () => {
    if (!dashboardUuid) throw new Error('Cannot refresh the embed token without a dashboard uuid');
    const constraints = resolvedConstraintsRef.current;
    const result = await ReportsApi.getDashboardEmbedToken({
      dashboardUuid,
      ...(constraints ? { constraints } : {}),
    });
    return result.token;
  }, [dashboardUuid]);

  // Detect when popup window is closed externally
  useEffect(() => {
    if (!isPopupOpen) return;

    const interval = setInterval(() => {
      if (!windowRef.current || windowRef.current.closed) {
        setIsPopupOpen(false);
        windowRef.current = null;
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPopupOpen]);

  // Fetch embed token for inline mode
  useEffect(() => {
    if (displayMode === 'inline' && dashboardUuid && !tokenData) {
      loadFn(async () => {
        const result = await ReportsApi.getDashboardEmbedToken({
          dashboardUuid,
          ...(resolvedConstraints ? { constraints: resolvedConstraints } : {}),
        });
        setTokenData(result);
        return result;
      });
    }
  }, [displayMode, dashboardUuid, resolvedConstraints, loadFn, tokenData]);

  if (!dashboardUuid) {
    return <div className="text-danger">Dashboard UUID is missing configuration</div>;
  }

  const handleOpen = () => {
    if (isPopupOpen && windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
      return;
    }

    if (resolvedConstraints && resolvedConstraints.length > 0) {
      try {
        const storageKey = `superset_constraints_${dashboardUuid}`;
        sessionStorage.setItem(storageKey, JSON.stringify(resolvedConstraints));
      } catch (e) {
        console.error('Failed to save constraints to sessionStorage', e instanceof Error ? e.message : e);
      }
    }

    const url = `/dashboard/${dashboardUuid}`;
    const width = 1000;
    const heightWin = 800;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - heightWin / 2;

    const win = window.open(
      url,
      '_blank',
      `location=no,toolbar=no,menubar=no,status=no,directories=no,scrollbars=yes,resizable=yes,width=${width},height=${heightWin},left=${left},top=${top}`
    );
    if (win) {
      windowRef.current = win;
      setIsPopupOpen(true);
    }
  };

  const handleReturnToInline = () => {
    windowRef.current?.close();
    setIsPopupOpen(false);
    windowRef.current = null;
  };

  if (displayMode === 'inline') {
    if (loading) return <div>Cargando {title}...</div>;
    if (error) return <DashboardLoadFailed reason={error} />;

    const containerStyle = uiHeight
      ? { height: uiHeight, width: '100%' }
      : { width: '100%', minHeight: '400px' };

    if (tokenData) {
      if (isPopupOpen) {
        return (
          <div
            style={containerStyle}
            className="flex flex-col items-center justify-center border theme-border rounded-2xl bg-zinc-50 dark:bg-zinc-900/40"
          >
            <Icon icon="fa-external-link-alt fa-3x" className="mb-3 theme-text-muted" />
            <h5 className="theme-text-muted mb-3">{title} se encuentra abierto en otra ventana</h5>
            <Button color="primary" onClick={handleReturnToInline}>
              <Icon icon="fa-columns" className="mr-2" />
              Volver a vista Inline
            </Button>
          </div>
        );
      }

      return (
        <div style={containerStyle} className="relative">
          <div className="absolute" style={{ top: 10, right: 10, zIndex: 1000 }}>
            <Button
              color="light"
              size="sm"
              className="shadow-sm border theme-border"
              onClick={handleOpen}
              title="Abrir en ventana independiente"
            >
              <Icon icon="fa-external-link-alt" />
            </Button>
          </div>

          <Suspense fallback={<div>Cargando visualización...</div>}>
            <SupersetDashboard
              embeddedUuid={tokenData.embeddedUuid}
              initialToken={tokenData.token}
              fetchToken={refetchToken}
              url={tokenData.url}
              height={uiHeight}
              hideTitle={true}
              hideTab={true}
              hideChartControls={false}
            />
          </Suspense>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="flex items-center justify-center p-4 border theme-border rounded-2xl bg-zinc-50 dark:bg-zinc-900/40">
      <Button color={isPopupOpen ? 'success' : 'primary'} onClick={handleOpen}>
        <Icon icon={isPopupOpen ? 'fa-check' : 'fa-external-link-alt'} className="mr-2" />
        {isPopupOpen ? 'Abierto en popup' : `Abrir ${title}`}
      </Button>
    </div>
  );
}
